/**
 * TypeFormFields — input grid for TypeEditFormBody. Split out so the
 * containing form file stays under the 200-line CLAUDE.md budget.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D4 — Type / Domain UI.
 */
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { UserDefinedTypeKind } from '../../../../sql-resolver/types/ddlEdit';
import {
  describeKindLabel,
  type TypeFormValues,
} from './typeFormHelpers';

export interface TypeFormFieldsProps {
  readonly engine: SqlEngine;
  readonly values: TypeFormValues;
  readonly supportedKinds: ReadonlyArray<UserDefinedTypeKind>;
  readonly onChange: <K extends keyof TypeFormValues>(
    key: K,
    value: TypeFormValues[K],
  ) => void;
  readonly testId: string;
}

export function TypeFormFields(props: TypeFormFieldsProps) {
  const { engine, values, supportedKinds, onChange, testId } = props;
  const { kind } = values;
  const isOracleKind = kind === 'object' || kind === 'table-of' || kind === 'varray';

  return (
    <>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-name`} className="text-xs">
          Type name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${testId}-name`}
          data-testid={`${testId}-name`}
          value={values.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="my_type"
          className="h-8 font-mono text-xs"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${testId}-kind`} className="text-xs">
          Kind <span className="text-destructive">*</span>
        </Label>
        <Select
          value={kind}
          onValueChange={(v) => onChange('kind', v as UserDefinedTypeKind)}
        >
          <SelectTrigger
            id={`${testId}-kind`}
            data-testid={`${testId}-kind`}
            className="h-8 font-mono text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {supportedKinds.map((k) => (
              <SelectItem key={k} value={k}>{describeKindLabel(k)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {kind === 'enum' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-enum-values`} className="text-xs">
            Enum values (comma-separated) <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-enum-values`}
            data-testid={`${testId}-enum-values`}
            value={values.enumValues ?? ''}
            onChange={(e) => onChange('enumValues', e.target.value)}
            placeholder="ok, bad, great"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {kind === 'composite' || kind === 'object' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-attributes`} className="text-xs">
            Attributes — one per line, "name TYPE" <span className="text-destructive">*</span>
          </Label>
          <textarea
            id={`${testId}-attributes`}
            data-testid={`${testId}-attributes`}
            value={values.attributes ?? ''}
            onChange={(e) => onChange('attributes', e.target.value)}
            placeholder={kind === 'composite' ? 'street TEXT\ncity VARCHAR(64)' : 'name VARCHAR2(50)\nage NUMBER'}
            rows={3}
            className="min-h-[64px] resize-y rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
          />
        </div>
      ) : null}

      {kind === 'alias' || kind === 'domain' || kind === 'table-of' || kind === 'varray' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-baseType`} className="text-xs">
            Base type <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-baseType`}
            data-testid={`${testId}-baseType`}
            value={values.baseType ?? ''}
            onChange={(e) => onChange('baseType', e.target.value)}
            placeholder={kind === 'alias' ? 'DECIMAL(18, 2)' : 'NUMBER'}
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {kind === 'varray' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-varray-size`} className="text-xs">
            Size <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-varray-size`}
            data-testid={`${testId}-varray-size`}
            value={values.varraySize ?? ''}
            onChange={(e) => onChange('varraySize', e.target.value)}
            placeholder="10"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {kind === 'range' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${testId}-subtype`} className="text-xs">
            Subtype <span className="text-destructive">*</span>
          </Label>
          <Input
            id={`${testId}-subtype`}
            data-testid={`${testId}-subtype`}
            value={values.subtype ?? ''}
            onChange={(e) => onChange('subtype', e.target.value)}
            placeholder="float8"
            className="h-8 font-mono text-xs"
          />
        </div>
      ) : null}

      {kind === 'domain' ? (
        <>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${testId}-default`} className="text-xs">
              DEFAULT (verbatim expression — optional)
            </Label>
            <Input
              id={`${testId}-default`}
              data-testid={`${testId}-default`}
              value={values.defaultText ?? ''}
              onChange={(e) => onChange('defaultText', e.target.value)}
              placeholder="''"
              className="h-8 font-mono text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${testId}-notNull`}
              data-testid={`${testId}-notNull`}
              checked={values.notNull ?? false}
              onCheckedChange={(checked) => onChange('notNull', checked === true)}
            />
            <Label htmlFor={`${testId}-notNull`} className="text-xs">NOT NULL</Label>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${testId}-checks`} className="text-xs">
              CHECK expressions (one per line — optional)
            </Label>
            <textarea
              id={`${testId}-checks`}
              data-testid={`${testId}-checks`}
              value={values.checkExpressions ?? ''}
              onChange={(e) => onChange('checkExpressions', e.target.value)}
              placeholder="VALUE LIKE '%@%'"
              rows={2}
              className="min-h-[48px] resize-y rounded-md border border-input bg-background px-2 py-1 font-mono text-xs"
            />
          </div>
        </>
      ) : null}

      {engine === 'oracle' && isOracleKind ? (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${testId}-orReplace`}
            data-testid={`${testId}-orReplace`}
            checked={values.orReplace ?? false}
            onCheckedChange={(checked) => onChange('orReplace', checked === true)}
          />
          <Label htmlFor={`${testId}-orReplace`} className="text-xs">
            CREATE OR REPLACE TYPE
          </Label>
        </div>
      ) : null}
    </>
  );
}
